import { stringEquals } from './string.helper';

/**
 * Compares a number to a string or another number for equality.
 * @function equals
 * @param {number} num - The number to compare.
 * @param {string | number | null} value - The value to compare against.
 * @param {boolean} [insensitive=true] - Whether the comparison should be case-insensitive.
 * @returns {boolean} True if the values are equal, false otherwise.
 */
const equals = (
  num: number,
  value: string | number | null,
  insensitive = true,
): boolean => {
  return stringEquals(num.toString(), value, insensitive);
};

/**
 * Adds an ordinal suffix to a number.
 * @function numberWithOrdinal
 * @param {number} num - The number to add the ordinal suffix to.
 * @returns {string} The number with the ordinal suffix.
 */
const numberWithOrdinal = (num: number): string => {
  const value = Math.abs(Math.floor(num)); // Ensure a valid positive integer
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const v = value % 100;
  const suffix = v >= 11 && v <= 13 ? 'th' : suffixes[v % 10] || 'th';

  return `${value}${suffix}`;
};

/**
 * Formats a number in a compact notation.
 * @function numberAsCompact
 * @param {number} num - The number to format.
 * @returns {string} The formatted number in compact notation.
 */
const numberAsCompact = (num: number): string => {
  return Intl.NumberFormat('en', { notation: 'compact' }).format(
    Number(num || 0),
  );
};

/**
 * Divides a number into batches of a specified step size.
 * @function numberToBatches
 * @param {number} num - The number to divide into batches.
 * @param {number} step - The step size for each batch.
 * @returns {Array<{ start: number; end: number }>} An array of batches with start and end values.
 */
const numberToBatches = (
  num: number,
  step: number,
): Array<{ start: number; end: number }> => {
  const batches: { start: number; end: number }[] = [];

  for (let i = 0; i < num; i += step) {
    if (i + step > num) {
      batches.push({
        start: i,
        end: num,
      });

      continue;
    }

    batches.push({
      start: i,
      end: i + step,
    });
  }

  return batches;
};

export { equals, numberWithOrdinal, numberAsCompact, numberToBatches };
